import { Award } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { InstitutionMark } from "../../components/InstitutionMark";
import type { Institution } from "../../lib/institution";
import { formatCertificateIssuedOn } from "../../lib/learning/certificateVerify";
import type { SkillCertificateVerify } from "../../lib/learning/types";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";

export function CertificateDocument({
  institution,
  cert,
  verifyCode,
  strings: s,
  locale,
  className,
}: {
  institution: Institution;
  cert: SkillCertificateVerify;
  verifyCode: string;
  strings: PortalStrings;
  locale: "en" | "hi";
  className?: string;
}) {
  const scorePct = Math.round((cert.score ?? 0) * 100);
  const issued = cert.issued_at ? formatCertificateIssuedOn(cert.issued_at, locale) : null;
  const streak = cert.streak_days ?? 0;
  const consistency = Math.round(cert.consistency_pct ?? 0);
  const reviews = cert.topic_reviews ?? 0;
  const evidence = s.certEvidence(streak, consistency, reviews);
  const showPathway =
    !!cert.pathway_title &&
    cert.pathway_title.trim().toLowerCase() !== (cert.subject ?? "").trim().toLowerCase();

  return (
    <article
      className={cn(
        "border-primary/25 bg-card relative overflow-hidden rounded-[1.5rem] border-2 px-6 py-8 shadow-[var(--shadow-card)] sm:px-10",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-4 inset-y-4 rounded-[1.15rem] border border-[hsl(var(--primary)/0.12)]"
      />
      <div className="relative space-y-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <InstitutionMark vertical={institution.vertical} name={institution.name} size="md" />
          <p className="text-muted-foreground text-[0.7rem] font-medium uppercase tracking-[0.18em]">
            {institution.name}
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-primary inline-flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em]">
            <Award className="size-3.5" aria-hidden />
            {s.verifiedCredential}
          </p>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            {s.certificateOfMastery}
          </h1>
        </div>
        <div className="space-y-1">
          <p className="font-display text-xl font-semibold sm:text-2xl">{cert.topic}</p>
          <p className="text-muted-foreground text-sm">{cert.subject}</p>
          {showPathway && <p className="text-muted-foreground text-xs">{cert.pathway_title}</p>}
        </div>
        <p className="font-display text-primary text-5xl font-semibold tabular-nums">{scorePct}%</p>
        {evidence && <p className="text-muted-foreground text-xs tabular-nums">{evidence}</p>}
        {issued && cert.issued_at && (
          <p className="text-muted-foreground text-xs">
            {s.issuedOn}{" "}
            <time dateTime={cert.issued_at} title={cert.issued_at}>
              {issued}
            </time>
          </p>
        )}
        <p className="text-muted-foreground break-all font-mono text-[0.7rem]">
          {s.verifyCodeLabel} {verifyCode}
        </p>
        <p className="text-muted-foreground mx-auto max-w-sm text-[0.7rem] leading-relaxed">
          {s.thisCredentialIsPublic}
        </p>
      </div>
    </article>
  );
}
